import { useFrame } from '@react-three/fiber';
import gsap from 'gsap';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Group } from 'three';

import type { GroupProps } from 'types/r3f';

import useMatrixUpdateHandler from 'hooks/useMatrixUpdateHandler';

export interface AnimatePresenceRef extends Group {}
export interface AnimatePresenceProps extends GroupProps {
  getIsPresent: () => boolean;

  showAnimation?: (wrapper: Group) => gsap.core.Timeline;
  hideAnimation?: (wrapper: Group) => gsap.core.Timeline;
}

const dumbAnimation = (group: Group) => gsap.timeline().to(group, {});

const AnimatePresence = forwardRef<AnimatePresenceRef, AnimatePresenceProps>(
  (
    {
      getIsPresent,
      children,
      showAnimation = dumbAnimation,
      hideAnimation = dumbAnimation,
      ...props
    },
    ref
  ) => {
    const groupRef = useRef<Group>(null!);
    useImperativeHandle(ref, () => groupRef.current);

    const localIsPresent = useRef<boolean>(getIsPresent());

    useFrame(() => {
      if (localIsPresent.current === getIsPresent()) return;
      localIsPresent.current = getIsPresent();

      if (getIsPresent()) {
        groupRef.current.visible = true;
        showAnimation(groupRef.current);
      } else {
        hideAnimation(groupRef.current).call(() => {
          groupRef.current.visible = false;
        });
      }
    });

    useMatrixUpdateHandler(getIsPresent, groupRef.current);

    return (
      <group {...props} ref={groupRef}>
        {children}
      </group>
    );
  }
);

AnimatePresence.displayName = 'AnimatePresence';
export default AnimatePresence;
